import {
  buildCollectionReportSelectionsSummaryJson,
  buildCollectionReportSelectedFeedsTree,
  formatCollectionReportCompareItemLabel,
  formatCollectionReportDate,
  formatCollectionReportRefreshHistoryLabel,
  normalizeCollectionReportSceneLabel,
  resolveCollectionReportCurrentSceneLabel,
} from './collection-report.utils';

type FeedPanelLike = {
  title?: unknown;
};

export type CollectionReportSelectionsSummaryBuilderInput = {
  collection: any;
  currentUser: any;
  isFeedTasking: boolean;
  isCompare: boolean;
  selectedValue?: any;
  selectionLabel?: any;
  defaultSubdailyCount?: any;
  selectedSubDailyItemsNew?: any[];
  subDailyFinalArray?: any[];
  selectedCheckboxesNew?: any[];
  selectedSceneNew?: any;
  selectedHistory?: Record<string, any> | null;
  scenesNew?: any[];
  filteredCollection?: any;
  leftData?: any[];
  rightData?: any[];
  leftFeedPanels?: Map<string, FeedPanelLike>;
  rightFeedPanels?: Map<string, FeedPanelLike>;
  pastPrediction?: boolean;
};

function uniq(xs: string[]): string[] {
  return Array.from(new Set(xs));
}

function summarizeCompareItemsForReport(items: any[]) {
  const safeItems = Array.isArray(items) ? items : [];
  const mapped = safeItems.map((it: any) => {
    const rawScenes = Array.isArray(it?.scenes) ? it.scenes : [];
    const selectedScenes = rawScenes
      .filter((sc: any) => sc?.checked === true || sc?.isChecked === true)
      .map((sc: any) => ({
        id: sc?.id ?? sc?.scene_id ?? null,
        name: sc?.name ?? sc?.sceneName ?? sc?.id ?? sc?.scene_id ?? '-',
      }));

    return {
      date: it?.date ?? null,
      start: it?.start ?? null,
      end: it?.end ?? null,
      name: it?.name ?? null,
      pane: it?.pane ?? null,
      scenes: selectedScenes,
      scenesTotal: rawScenes.length,
      scenesTruncated: false,
    };
  });

  return {
    items: mapped,
    itemsTotal: safeItems.length,
    itemsTruncated: false,
  };
}

function summarizeFeedPanelsForReport(panels: Map<string, FeedPanelLike> | undefined) {
  const titlesRaw: string[] =
    panels instanceof Map
      ? Array.from(panels.values())
          .map((panel) => String(panel?.title || '').trim())
          .filter(Boolean)
      : [];

  return {
    feeds: uniq(titlesRaw),
  };
}

export function buildCollectionReportSelectionsSummaryText(
  input: CollectionReportSelectionsSummaryBuilderInput,
): string {
  const feedTree = buildCollectionReportSelectedFeedsTree({
    collection: input.collection,
    currentUser: input.currentUser,
    isFeedTasking: input.isFeedTasking,
  });
  const repeatFrequency = String(input.collection?.repeatFrequency ?? '').trim();
  const selectedSensorLabels = Array.isArray(feedTree?.s)
    ? feedTree.s.map((sensor: any) => String(sensor?.n ?? '').trim()).filter(Boolean)
    : [];

  const selectedValueRaw = input.selectedValue;
  const recurrencyAll = ((input.collection as any)?.recurrencyArray ?? []).filter(
    (item: any) => item?.checked === true || item?.isChecked === true,
  );
  const selectedSubDailyItems = Array.isArray(input.selectedSubDailyItemsNew)
    ? input.selectedSubDailyItemsNew
    : [];
  const selectedSubDailyItemLabels = selectedSubDailyItems.slice(0, 5).map((item: any) => {
    if (item == null) return '-';
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    if (item?.value != null) return String(item.value);
    return item?.name ?? item?.label ?? item?.date ?? item?.id ?? '-';
  });
  const selectedSubDailyCounts = uniq(
    selectedSubDailyItems
      .map((item: any) => {
        if (item == null) return null;
        if (typeof item === 'number') return String(item);
        if (typeof item === 'string') return item.trim() || null;
        if (item?.value != null) return String(item.value);
        return null;
      })
      .filter(Boolean) as string[],
  );
  const subDailyFinalSelections = Array.isArray(input.subDailyFinalArray)
    ? input.subDailyFinalArray
    : [];
  const selectedDateEntries = Array.isArray(input.selectedCheckboxesNew)
    ? input.selectedCheckboxesNew
    : [];
  const hasExplicitDateSelection = recurrencyAll.length > 0 || selectedDateEntries.length > 0;
  const isSingleDateNonCompare = !input.isCompare && (repeatFrequency === 'Daily' || repeatFrequency === 'Sub-daily');
  const fallbackDailySelection = isSingleDateNonCompare && hasExplicitDateSelection
    ? formatCollectionReportDate(input.selectionLabel ?? selectedValueRaw?.date ?? selectedValueRaw?.start ?? null)
    : '';
  const fallbackSubDailySelection =
    repeatFrequency === 'Sub-daily' && input.defaultSubdailyCount != null && input.defaultSubdailyCount !== 0
      ? `Sub-daily ${input.defaultSubdailyCount}`
      : '';

  const formatDailyRecurrencySelectionLabel = (item: any): string => {
    if (isSingleDateNonCompare) {
      const singleDate = item?.date ?? item?.start ?? selectedValueRaw?.date ?? selectedValueRaw?.start ?? null;
      return formatCollectionReportDate(singleDate);
    }

    return formatCollectionReportCompareItemLabel(item);
  };

  const formatSubDailySelectionLabel = (value: unknown, item?: any): string => {
    const subDailyValue = value == null ? null : String(value).trim();
    if (!subDailyValue) return '-';
    if (isSingleDateNonCompare) {
      return `Sub-daily ${subDailyValue}`;
    }

    return formatCollectionReportCompareItemLabel({
      subDailyCount: subDailyValue,
      start: item?.start ?? selectedDateEntries[0]?.start ?? selectedValueRaw?.start ?? null,
      end: item?.end ?? selectedDateEntries[0]?.end ?? selectedValueRaw?.end ?? null,
    });
  };

  const dailyRecurrencySelections = uniq(
    [
      ...(recurrencyAll.length ? recurrencyAll : selectedDateEntries)
        .map((item: any) => formatDailyRecurrencySelectionLabel(item))
        .filter((label: string) => !!label && label !== '-' && label !== 'Selection'),
      ...(fallbackDailySelection && fallbackDailySelection !== '-' ? [fallbackDailySelection] : []),
    ],
  ).slice(0, 5);

  const subDailySelections = uniq([
    ...selectedSubDailyItemLabels,
    ...subDailyFinalSelections.map((item: any) =>
      formatSubDailySelectionLabel(item?.sequenceNumber ?? item?.value ?? null, item),
    ),
    ...selectedSubDailyCounts.map((value: string) => formatSubDailySelectionLabel(value)),
    ...(fallbackSubDailySelection ? [fallbackSubDailySelection] : []),
  ])
    .filter((label: string) => !!label && label !== '-' && label !== 'Selection')
    .slice(0, 5);

  const currentSceneRaw = input.selectedSceneNew ?? null;
  const currentScene = currentSceneRaw
    ? {
        id: currentSceneRaw?.id ?? currentSceneRaw?.scene_id ?? null,
        label:
          normalizeCollectionReportSceneLabel(currentSceneRaw?.sceneName ?? currentSceneRaw?.name ?? currentSceneRaw?.id) || null,
      }
    : null;

  const selectedHistoryObj = input.selectedHistory && typeof input.selectedHistory === 'object' ? input.selectedHistory : {};
  const selectedHistoryIds = Object.keys(selectedHistoryObj);
  const refreshSelectedRaw: Array<{ refreshId: string; dateTime: any }> = [];
  const sensorsAll = Array.isArray(input.collection?.sensors) ? input.collection.sensors : [];
  const sensorsWithRefreshSelected: any[] = [];

  for (const sensor of sensorsAll) {
    const refreshHistory = Array.isArray(sensor?.refreshHistory) ? sensor.refreshHistory : [];
    for (const item of refreshHistory) {
      const refreshId = item?.refreshId != null ? String(item.refreshId) : '';
      if (!refreshId) continue;

      const checked = item?.checked === true || item?.isChecked === true;
      const selectedByData = selectedHistoryIds.includes(refreshId);
      if (!checked && !selectedByData) continue;

      if (!sensorsWithRefreshSelected.includes(sensor)) sensorsWithRefreshSelected.push(sensor);
      const dateTime =
        item?.name ??
        item?.refreshDateTime ??
        item?.datetime ??
        item?.dateTime ??
        item?.timestamp ??
        item?.createdAt ??
        item?.time ??
        item?.date ??
        null;
      refreshSelectedRaw.push({ refreshId, dateTime });
    }
  }

  const refreshSelectedUniq = Array.from(new Map(refreshSelectedRaw.map((item) => [item.refreshId, item])).values());
  const checkedScenesRaw = sensorsAll
    .filter((sensor: any) => sensor?.checked === true || sensor?.isChecked === true)
    .flatMap((sensor: any) => (Array.isArray(sensor?.scenes) ? sensor.scenes : []))
    .filter((scene: any) => scene?.checked === true || scene?.isChecked === true)
    .map((scene: any) => normalizeCollectionReportSceneLabel(scene?.sceneName ?? scene?.name ?? scene?.id))
    .filter(Boolean);
  const checkedScenes = uniq(checkedScenesRaw).slice(0, 5);

  const historySceneLabel = (() => {
    for (const sensor of sensorsWithRefreshSelected) {
      const firstScene = Array.isArray(sensor?.scenes) && sensor.scenes.length ? sensor.scenes[0] : null;
      const label = normalizeCollectionReportSceneLabel(firstScene?.sceneName ?? firstScene?.name ?? firstScene?.id);
      if (label) return label;
    }
    return null;
  })();

  const scenesNewRaw = Array.isArray(input.scenesNew) ? input.scenesNew : [];
  const getPane = (scene: any): 'left' | 'right' | null => {
    const pane = scene?.pane ?? scene?.mapPane ?? scene?.scene?.pane ?? scene?.scene?.mapPane ?? null;
    return pane === 'left' || pane === 'right' ? pane : null;
  };
  const getSeq = (scene: any): number | null => {
    const raw =
      scene?.scene?.sequenceNumber ??
      scene?.scene?.sequence_number ??
      scene?.scene?.sequence ??
      scene?.sequenceNumber ??
      scene?.sequence ??
      null;
    const value = raw == null ? NaN : Number(raw);
    return Number.isFinite(value) ? value : null;
  };
  const getSceneId = (scene: any): any => scene?.id ?? scene?.scene?.id ?? scene?.scene_id ?? scene?.scene?.scene_id ?? null;
  const getSceneName = (scene: any): string => {
    const name =
      scene?.scene?.sceneName ??
      scene?.scene?.name ??
      scene?.sceneName ??
      scene?.name ??
      getSceneId(scene) ??
      '-';
    return String(name);
  };
  const summarizePaneScenes = (pane: 'left' | 'right') => {
    const all = scenesNewRaw.filter((scene: any) => getPane(scene) === pane);
    const limit = 10;
    return {
      scenes: all.slice(0, limit).map((scene: any) => ({ id: getSceneId(scene), name: getSceneName(scene) })),
      scenesTotal: all.length,
      scenesTruncated: all.length > limit,
    };
  };

  const filteredSensors = Array.isArray(input.filteredCollection?.sensors) ? input.filteredCollection.sensors : [];
  const summarizeSubDailyScenesFor = (pane: 'left' | 'right', subDailyValue: any) => {
    const seqWanted = subDailyValue == null ? null : Number(subDailyValue);
    const wanted = Number.isFinite(seqWanted) ? seqWanted : null;
    const limit = 10;

    const fromScenesNew = scenesNewRaw
      .filter((scene: any) => {
        const seq = getSeq(scene);
        if (wanted == null || seq == null) return false;
        const paneVal = getPane(scene);
        return seq === wanted && (paneVal == null || paneVal === pane);
      })
      .map((scene: any) => ({ id: getSceneId(scene), name: getSceneName(scene) }));

    if (fromScenesNew.length) {
      return {
        scenes: fromScenesNew.slice(0, limit),
        scenesTotal: fromScenesNew.length,
        scenesTruncated: fromScenesNew.length > limit,
      };
    }

    const fromFiltered = filteredSensors
      .flatMap((sensor: any) => (Array.isArray(sensor?.scenes) ? sensor.scenes : []))
      .filter((scene: any) => {
        const seq = scene?.sequenceNumber ?? scene?.sequence_number ?? scene?.sequence ?? null;
        const value = seq == null ? NaN : Number(seq);
        return wanted != null && Number.isFinite(value) && value === wanted;
      })
      .map((scene: any) => ({
        id: scene?.id ?? null,
        name: String(scene?.sceneName ?? scene?.name ?? scene?.id ?? '-'),
      }));

    return {
      scenes: fromFiltered.slice(0, limit),
      scenesTotal: fromFiltered.length,
      scenesTruncated: fromFiltered.length > limit,
    };
  };

  const subDailyItemsWithPane = selectedSubDailyItems
    .map((item: any) => ({
      value: item?.value ?? item,
      pane: item?.mapPane ?? item?.pane ?? null,
    }))
    .filter((item: any) => item?.value != null && (item?.pane === 'left' || item?.pane === 'right'));
  const subDailyCompareEnabled = input.isCompare && subDailyItemsWithPane.length >= 2;
  const subDailyLeft = subDailyItemsWithPane.find((item: any) => item.pane === 'left') ?? null;
  const subDailyRight = subDailyItemsWithPane.find((item: any) => item.pane === 'right') ?? null;

  const activeRangeForCompare = (() => {
    const leftItem = Array.isArray(input.leftData) && input.leftData.length ? input.leftData[0] : null;
    const raw = leftItem ?? selectedValueRaw;
    return {
      start: raw?.start ?? null,
      end: raw?.end ?? null,
    };
  })();

  const sensorList = Array.isArray(input.collection?.sensors) ? input.collection.sensors : [];
  const indicesSelectedProducts = uniq(
    sensorList
      .flatMap((sensor: any) => (Array.isArray(sensor?.indicesDetails) ? sensor.indicesDetails : []))
      .filter((item: any) => item?.checked === true || item?.isChecked === true)
      .map((item: any) => String(item?.product ?? item?.name ?? '').trim())
      .filter(Boolean),
  );
  const superResSelectedProducts = uniq(
    sensorList
      .flatMap((sensor: any) => (Array.isArray(sensor?.superResolutions) ? sensor.superResolutions : []))
      .filter((item: any) => item?.checked === true || item?.isChecked === true)
      .map((item: any) => {
        const product = String(item?.product ?? item?.name ?? '').trim();
        const resolution = String(item?.resolution ?? '').trim();
        if (product && resolution) return `${product} (${resolution})`;
        return product || resolution;
      })
      .filter(Boolean),
  );
  const valueAddedSelectedProducts = uniq([...indicesSelectedProducts, ...superResSelectedProducts]);

  const buildCompareSelectionSnapshot = () => {
    if (!input.isCompare) return null;

    const base = {
      left: {
        ...summarizeCompareItemsForReport(input.leftData as any[]),
        ...summarizeFeedPanelsForReport(input.leftFeedPanels),
      },
      right: {
        ...summarizeCompareItemsForReport(input.rightData as any[]),
        ...summarizeFeedPanelsForReport(input.rightFeedPanels),
      },
    };

    if (subDailyCompareEnabled && (!base.right.items?.length || !subDailyRight)) {
      const leftScenes = summarizeSubDailyScenesFor('left', subDailyLeft?.value) ?? summarizePaneScenes('left');
      const rightScenes = summarizeSubDailyScenesFor('right', subDailyRight?.value) ?? summarizePaneScenes('right');
      return {
        left: {
          items: [
            {
              subDailyCount: subDailyLeft?.value ?? null,
              start: activeRangeForCompare.start,
              end: activeRangeForCompare.end,
              ...leftScenes,
            },
          ],
          itemsTotal: 1,
          itemsTruncated: false,
          ...summarizeFeedPanelsForReport(input.leftFeedPanels),
        },
        right: {
          items: subDailyRight
            ? [
                {
                  subDailyCount: subDailyRight?.value ?? null,
                  start: activeRangeForCompare.start,
                  end: activeRangeForCompare.end,
                  ...rightScenes,
                },
              ]
            : [],
          itemsTotal: subDailyRight ? 1 : 0,
          itemsTruncated: false,
          ...summarizeFeedPanelsForReport(input.rightFeedPanels),
        },
      };
    }

    return base;
  };

  const summarizeCompareSideForReport = (items: any[], panels: any) => {
    const safeItems = Array.isArray(items) ? items : [];
    const feeds = Array.isArray(panels?.feeds) ? panels.feeds : [];
    const mappedItems = safeItems.map((item: any) => ({
      label: formatCollectionReportCompareItemLabel(item),
      scenes: Array.isArray(item?.scenes)
        ? item.scenes.map(
            (scene: any) => normalizeCollectionReportSceneLabel(scene?.name ?? scene?.sceneName ?? scene?.id) || String(scene?.id ?? '-'),
          )
        : [],
      feeds,
    }));

    return {
      items: mappedItems,
    };
  };

  const currentSceneLabel =
    currentScene?.label || historySceneLabel || checkedScenes.length
      ? resolveCollectionReportCurrentSceneLabel({
          currentSceneLabel: currentScene?.label,
          historySceneLabel,
          checkedScenes,
        })
      : '';
  const refreshHistoryLabels = refreshSelectedUniq.map((item) =>
    formatCollectionReportRefreshHistoryLabel({
      dateTime: item?.dateTime,
      refreshId: item?.refreshId,
    }),
  );
  const compareSelectionSnapshotForReport = buildCompareSelectionSnapshot();
  const compareSelectionsForReport = compareSelectionSnapshotForReport
    ? {
        left: summarizeCompareSideForReport(compareSelectionSnapshotForReport?.left?.items, compareSelectionSnapshotForReport?.left),
        right: summarizeCompareSideForReport(compareSelectionSnapshotForReport?.right?.items, compareSelectionSnapshotForReport?.right),
      }
    : undefined;

  return buildCollectionReportSelectionsSummaryJson({
    feedTree,
    selectedSensors: selectedSensorLabels,
    dailyRecurrencySelections,
    subDailySelections,
    currentSceneLabel,
    refreshHistory: refreshHistoryLabels,
    pastPredictionsEnabled: !!input.pastPrediction,
    compare: compareSelectionsForReport,
    valueAddedProducts: valueAddedSelectedProducts,
  });
}